import React, { useState, useEffect } from 'react';
import { getEmployees, submitTraining, API_BASE_URL } from '../utils/api';
import axios from 'axios';
import './TrainingForm.css';

function TrainingForm() {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [todaySessions, setTodaySessions] = useState([]);
  const [joinedSessionId, setJoinedSessionId] = useState(null);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [attachmentPreviews, setAttachmentPreviews] = useState([]);
  const [isGuest, setIsGuest] = useState(false);
  const [isExternalTraining, setIsExternalTraining] = useState(false);
  const [trainingCategory, setTrainingCategory] = useState(''); // 'MTB' or 'DPT'
  const [formData, setFormData] = useState({
    department: '',
    employee_id: '',
    guest_name: '',
    guest_department: '',
    role: 'trainee', // 'trainer' or 'trainee'
    training_date: new Date().toISOString().split('T')[0],
    training_topic: '',
    training_hours: '',
    training_minutes: '',
    trainer_name: '',
    notes: ''
  });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [submittedData, setSubmittedData] = useState(null);

  useEffect(() => {
    loadEmployees();
    loadTodaySessions();
  }, []);

  // Reload today's sessions when role changes to trainee
  useEffect(() => {
    if (formData.role === 'trainee') {
      loadTodaySessions();

      // Auto-refresh every minute to update timers and remove expired sessions
      const interval = setInterval(() => {
        loadTodaySessions();
      }, 60000); // 60 seconds

      return () => clearInterval(interval);
    }
  }, [formData.role]);

  const loadTodaySessions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/training/today-sessions`);
      setTodaySessions(response.data.sessions || []);
    } catch (err) {
      console.error('Failed to load today sessions:', err);
      setTodaySessions([]);
    }
  };

  const formatTimeRemaining = (minutes) => {
    if (minutes <= 0) return 'Expired';
    if (minutes < 60) return `${minutes} min left`;

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (mins === 0) return `${hours}h left`;
    return `${hours}h ${mins}m left`;
  };

  const handleJoinSession = (session) => {
    // Convert decimal hours to hours and minutes
    const totalHours = session.training_hours;
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);

    setFormData(prev => ({
      ...prev,
      training_topic: session.training_topic,
      training_hours: hours.toString(),
      training_minutes: minutes.toString(),
      trainer_name: session.trainer_name,
      training_date: session.training_date,
      notes: session.notes || ''
    }));

    // Store session ID to link trainee with trainer
    setJoinedSessionId(session.id);

    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadEmployees = async () => {
    try {
      const response = await getEmployees();
      setEmployees(response.data);

      // Extract unique departments
      const uniqueDepts = [...new Set(response.data.map(emp => emp.department))].sort();
      setDepartments(uniqueDepts);
    } catch (err) {
      setError('Failed to load employee data');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'department') {
      // Filter employees by department
      const filtered = employees.filter(emp => emp.department === value);
      setFilteredEmployees(filtered);
      // Reset employee selection when department changes
      setFormData(prev => ({ ...prev, employee_id: '' }));
      setSelectedEmployee(null);
    }

    if (name === 'employee_id') {
      const emp = employees.find(e => e.employee_id === parseInt(value));
      setSelectedEmployee(emp || null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    // Validate the subject: either a selected employee or a guest name.
    if (isGuest) {
      if (!formData.guest_name.trim()) {
        setError('Please enter your name');
        setLoading(false);
        return;
      }
    } else if (!formData.employee_id) {
      setError('Please select an employee');
      setLoading(false);
      return;
    }

    // Convert hours and minutes to decimal hours
    const hours = parseInt(formData.training_hours) || 0;
    const minutes = parseInt(formData.training_minutes) || 0;
    const totalHours = hours + (minutes / 60);

    if (totalHours <= 0) {
      setError('Please enter training duration');
      setLoading(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      if (isGuest) {
        formDataToSend.append('guest_name', formData.guest_name);
        formDataToSend.append('guest_department', formData.guest_department);
      } else {
        formDataToSend.append('employee_id', formData.employee_id);
      }
      formDataToSend.append('role', formData.role);
      formDataToSend.append('training_date', formData.training_date);
      formDataToSend.append('training_topic', formData.training_topic);
      formDataToSend.append('training_hours', totalHours);
      // For external training, trainer_name is optional
      if (!isExternalTraining && formData.trainer_name) {
        formDataToSend.append('trainer_name', formData.trainer_name);
      }
      formDataToSend.append('notes', formData.notes);
      if (joinedSessionId) {
        formDataToSend.append('session_id', joinedSessionId);
      }
      // Mark as external training
      if (isExternalTraining) {
        formDataToSend.append('is_external', 'true');
      }
      // Add training category (only for trainers)
      if (formData.role === 'trainer' && trainingCategory) {
        formDataToSend.append('training_category', trainingCategory);
      }
      // Add multiple attachment files
      if (attachmentFiles.length > 0) {
        attachmentFiles.forEach((file) => {
          formDataToSend.append('attachments', file);
        });
      }

      await submitTraining(formDataToSend);

      setSuccess(true);
      setSubmittedData({
        role: formData.role,
        training_topic: formData.training_topic,
        training_hours: totalHours,
        trainer_name: isExternalTraining ? 'External Training' : formData.trainer_name,
        isGuest: isGuest,
        isExternal: isExternalTraining,
        employee_name: selectedEmployee?.employee_name || formData.guest_name,
        department: selectedEmployee?.department || formData.guest_department
      });
    } catch (err) {
      // Check error types
      const errorMessage = err.response?.data?.error || 'Failed to save training record';
      if (errorMessage.includes('already joined')) {
        setError('duplicate-join');
      } else if (errorMessage.includes('must join a training session') && !isExternalTraining) {
        setError('must-join-session');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      {/* Must Join Session Error Modal */}
      {error === 'must-join-session' && (
        <div className="modal-overlay" onClick={() => setError('')}>
          <div className="modal-content error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-error">
              <div className="error-icon-large">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <h2>Cannot Create Training Record</h2>
              <h2 className="thai-text">ไม่สามารถบันทึกได้</h2>
            </div>
            <div className="modal-body-error">
              <p><strong>Trainees must join a training session created by a trainer</strong></p>
              <p className="thai-text"><strong>ผู้เข้ารับการอบรมต้องเข้าร่วม session ที่วิทยากรสร้างไว้เท่านั้น</strong></p>
              <p style={{ marginTop: '1rem', fontSize: '0.9rem', opacity: 0.8 }}>
                Please scroll down to "Today's Training Sessions" and click "Join" on an available session.
              </p>
              <p className="thai-text" style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                กรุณาเลื่อนลงไปที่ "ห้องเทรนวันนี้" และกด "เข้าร่วม" session ที่ต้องการ
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn-primary" onClick={() => setError('')}>
                OK / ตกลง
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Join Error Modal */}
      {error === 'duplicate-join' && (
        <div className="modal-overlay" onClick={() => setError('')}>
          <div className="modal-content error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-error">
              <div className="error-icon-large">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              </div>
              <h2>Already Joined!</h2>
            </div>
            <div className="modal-body-error">
              <p>คุณได้เข้าร่วม training session นี้แล้ว</p>
              <p>ไม่สามารถเข้าร่วมซ้ำได้</p>
            </div>
            <div className="modal-actions">
              <button className="btn-primary" onClick={() => setError('')}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {success && submittedData ? (
        // Success Screen
        <div className="success-screen">
          <div className="success-icon">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="8 12 11 15 16 9"></polyline>
            </svg>
          </div>

          <h1 className="success-title">
            {submittedData.role === 'trainer' ? 'Training Session Created!' : 'Successfully Joined!'}
          </h1>

          <p className="success-subtitle">
            {submittedData.role === 'trainer'
              ? 'Your training session has been created. Trainees can now join.'
              : 'You have successfully joined the training session.'}
          </p>

          <div className="success-details">
            <div className="detail-row">
              <span className="detail-label">Topic</span>
              <span className="detail-value">{submittedData.training_topic}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Duration</span>
              <span className="detail-value">
                {(() => {
                  const h = Math.floor(submittedData.training_hours);
                  const m = Math.round((submittedData.training_hours - h) * 60);
                  return h > 0 && m > 0 ? `${h} hour${h > 1 ? 's' : ''} ${m} minute${m > 1 ? 's' : ''}`
                       : h > 0 ? `${h} hour${h > 1 ? 's' : ''}`
                       : `${m} minute${m > 1 ? 's' : ''}`;
                })()}
              </span>
            </div>
            {submittedData.role === 'trainee' && submittedData.trainer_name && (
              <div className="detail-row">
                <span className="detail-label">Trainer</span>
                <span className="detail-value">{submittedData.trainer_name}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Participant</span>
              <span className="detail-value">
                {submittedData.employee_name}
                {submittedData.department && ` • ${submittedData.department}`}
              </span>
            </div>
          </div>

          <div className="success-actions">
            <button
              className="btn-primary"
              onClick={() => {
                setSuccess(false);
                setSubmittedData(null);
                setFormData({
                  department: '',
                  employee_id: '',
                  guest_name: '',
                  guest_department: '',
                  role: 'trainee',
                  training_date: new Date().toISOString().split('T')[0],
                  training_topic: '',
                  training_hours: '',
                  training_minutes: '',
                  trainer_name: '',
                  notes: ''
                });
                setIsGuest(false);
                setSelectedEmployee(null);
                setFilteredEmployees([]);
                setJoinedSessionId(null);
                setAttachmentFiles([]);
                setAttachmentPreviews([]);
                setError('');
              }}
            >
              Log Another Session
            </button>
          </div>
        </div>
      ) : (
        // Original Form
      <div className="form-card">
        <div className="form-header">
          <span className="eyebrow-pill">Training Record / บันทึกการเทรน</span>
          <h1 className="form-title">
            Log Your <em>Training</em> Hours
          </h1>
          <p className="form-subtitle">Record your training and skill development activities<br/>บันทึกการเทรนและพัฒนาทักษะของคุณ</p>
        </div>

        {error && (
          <div className="alert alert-error">
            ✗ {error}
          </div>
        )}

        {/* Today's Training Sessions - Only show for trainees */}
        {formData.role === 'trainee' && todaySessions.length > 0 && (
          <div className="today-sessions-section">
            <div className="sessions-header">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <h3>Today's Training Sessions / ห้องเทรนวันนี้</h3>
              <span className="sessions-badge">{todaySessions.length}</span>
            </div>
            <div className="sessions-list">
              {todaySessions.map((session) => (
                <div key={session.id} className="session-card" onClick={() => handleJoinSession(session)}>
                  <div className="session-main">
                    <div className="session-topic">{session.training_topic}</div>
                    <div className="session-trainer">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                        <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                      </svg>
                      by {session.trainer_name}
                    </div>
                  </div>
                  <div className="session-info">
                    <div className="session-time-info">
                      <div className="session-hours">
                        {(() => {
                          const h = Math.floor(session.training_hours);
                          const m = Math.round((session.training_hours - h) * 60);
                          return h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : `${m}m`;
                        })()}
                      </div>
                      {session.time_remaining_minutes && (
                        <div className="session-timer">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                          </svg>
                          {formatTimeRemaining(session.time_remaining_minutes)}
                        </div>
                      )}
                    </div>
                    <button type="button" className="join-btn">
                      Join / เข้าร่วม →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Joined Session Indicator */}
        {joinedSessionId && (
          <div className="joined-session-indicator">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <span>Joined a training session - form pre-filled! / เข้าร่วมห้องเทรนแล้ว - ฟอร์มถูกกรอกให้อัตโนมัติ!</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="training-form">
          {/* Guest toggle: for casual staff / trainees not in the employee list */}
          <div className="form-group">
            <label className="guest-toggle">
              <input
                type="checkbox"
                checked={isGuest}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsGuest(checked);
                  setError('');
                  // Clear the fields belonging to the other mode so a stale
                  // value can't be submitted after switching.
                  if (checked) {
                    setFormData(prev => ({ ...prev, department: '', employee_id: '' }));
                    setSelectedEmployee(null);
                    setFilteredEmployees([]);
                  } else {
                    setFormData(prev => ({ ...prev, guest_name: '', guest_department: '' }));
                  }
                }}
              />
              <span>I'm not in the employee list (Trainee/ Casual staff) / ฉันไม่ได้อยู่ในรายชื่อพนักงาน</span>
            </label>
          </div>

          {/* External Training toggle - moved up for clarity */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isExternalTraining}
                onChange={(e) => {
                  setIsExternalTraining(e.target.checked);
                  if (e.target.checked) {
                    // Force role to trainee for external training
                    setFormData(prev => ({ ...prev, role: 'trainee' }));
                  }
                }}
                disabled={joinedSessionId !== null}
              />
              <span>
                External Training / การเทรนภายนอก
                <span className="checkbox-hint">
                  (ไม่ต้องระบุผู้ฝึกสอน - บันทึกแค่ชั่วโมงเทรนที่ได้รับจากภายนอก)
                </span>
              </span>
            </label>
          </div>

          {isGuest ? (
            <>
              <div className="form-group">
                <label htmlFor="guest_name" className="form-label">
                  Your Full Name / ชื่อ-นามสกุล <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="guest_name"
                  name="guest_name"
                  value={formData.guest_name}
                  onChange={handleInputChange}
                  required
                  placeholder="First and last name"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="guest_department" className="form-label">
                  Department / แผนก
                </label>
                <input
                  type="text"
                  id="guest_department"
                  name="guest_department"
                  value={formData.guest_department}
                  onChange={handleInputChange}
                  placeholder="e.g., Housekeeping"
                  className="form-input"
                />
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="department" className="form-label">
                  Department / แผนก <span className="required">*</span>
                </label>
                <select
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  required={!isGuest}
                  className="form-select"
                >
                  <option value="">Select Department</option>
                  {departments.map(dept => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="employee_id" className="form-label">
                  Employee Name / ชื่อพนักงาน <span className="required">*</span>
                </label>
                <select
                  id="employee_id"
                  name="employee_id"
                  value={formData.employee_id}
                  onChange={handleInputChange}
                  required={!isGuest}
                  disabled={!formData.department}
                  className="form-select"
                >
                  <option value="">
                    {formData.department ? 'Select Employee' : 'Select department first'}
                  </option>
                  {filteredEmployees.map(emp => (
                    <option key={emp.employee_id} value={emp.employee_id}>
                      {emp.employee_name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedEmployee && (
                <div className="employee-info">
                  <div className="info-item">
                    <span className="info-label">Position / ตำแหน่ง:</span>
                    <span>{selectedEmployee.position}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Department / แผนก:</span>
                    <span>{selectedEmployee.department}</span>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="form-group">
            <label className="form-label">
              Your Role / บทบาท <span className="required">*</span>
            </label>
            <div className="role-selector">
              <label className="role-option">
                <input
                  type="radio"
                  name="role"
                  value="trainee"
                  checked={formData.role === 'trainee'}
                  onChange={handleInputChange}
                  disabled={joinedSessionId !== null}
                  className="role-radio"
                />
                <span className="role-label">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <span>
                    <strong>Trainee / ผู้เข้าร่วม</strong>
                    <small>I attended the training / ฉันเข้าร่วมเทรน</small>
                  </span>
                </span>
              </label>
              {!joinedSessionId && !isGuest && !isExternalTraining && (
                <label className="role-option">
                  <input
                    type="radio"
                    name="role"
                    value="trainer"
                    checked={formData.role === 'trainer'}
                    onChange={handleInputChange}
                    className="role-radio"
                  />
                  <span className="role-label">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                      <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                    </svg>
                    <span>
                      <strong>Trainer / วิทยากร</strong>
                      <small>I conducted the training / ฉันเป็นผู้สอน</small>
                    </span>
                  </span>
                </label>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="training_date" className="form-label">
              Training Date / วันที่เทรน <span className="required">*</span>
            </label>
            <input
              type="date"
              id="training_date"
              name="training_date"
              value={formData.training_date}
              onChange={handleInputChange}
              required
              disabled={joinedSessionId !== null}
              className="form-input"
            />
          </div>

          {/* Training Category Selection - Only show for Trainer role */}
          {formData.role === 'trainer' && (
            <div className="form-group">
              <label className="form-label">
                Training Category / ประเภทการเทรน <span className="required">*</span>
              </label>
              <div className="category-selector">
                <label className="category-option">
                  <input
                    type="radio"
                    name="training_category"
                    value="MTB"
                    checked={trainingCategory === 'MTB'}
                    onChange={(e) => setTrainingCategory(e.target.value)}
                    required
                  />
                  <span className="category-label">
                    <strong>MTB</strong>
                    <small>Minor Training Bite</small>
                  </span>
                </label>
                <label className="category-option">
                  <input
                    type="radio"
                    name="training_category"
                    value="DPT"
                    checked={trainingCategory === 'DPT'}
                    onChange={(e) => setTrainingCategory(e.target.value)}
                    required
                  />
                  <span className="category-label">
                    <strong>DPT</strong>
                    <small>Department Training</small>
                  </span>
                </label>
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="training_topic" className="form-label">
              Training Topic / หัวข้อเทรน <span className="required">*</span>
            </label>
            <input
              type="text"
              id="training_topic"
              name="training_topic"
              value={formData.training_topic}
              onChange={handleInputChange}
              required
              disabled={joinedSessionId !== null}
              placeholder="e.g., Customer Service Excellence"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Training Duration / ระยะเวลา <span className="required">*</span>
            </label>
            <div className="duration-inputs">
              <div className="duration-field">
                <input
                  type="number"
                  id="training_hours"
                  name="training_hours"
                  value={formData.training_hours}
                  onChange={handleInputChange}
                  min="0"
                  max="24"
                  placeholder="0"
                  disabled={joinedSessionId !== null}
                  className="form-input"
                />
                <span className="duration-label">Hours / ชั่วโมง</span>
              </div>
              <div className="duration-separator">:</div>
              <div className="duration-field">
                <input
                  type="number"
                  id="training_minutes"
                  name="training_minutes"
                  value={formData.training_minutes}
                  onChange={handleInputChange}
                  min="0"
                  max="59"
                  placeholder="0"
                  disabled={joinedSessionId !== null}
                  className="form-input"
                />
                <span className="duration-label">Minutes / นาที</span>
              </div>
            </div>
          </div>


          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes / หมายเหตุ
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows="3"
              placeholder="Additional information (optional)"
              className="form-textarea"
            ></textarea>
          </div>

          <div className="form-group">
            <label htmlFor="attachment" className="form-label">
              Attachments (Optional) / ไฟล์แนบ (ไม่บังคับ)
            </label>
            <p className="form-helper-text">Upload multiple photos or documents (Max 5MB each: JPG, PNG, PDF, DOC, DOCX)<br/>อัพโหลดรูปภาพหรือเอกสารหลายไฟล์ (สูงสุดไฟล์ละ 5MB)</p>
            <input
              type="file"
              id="attachment"
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files);
                if (files.length > 0) {
                  // Validate each file
                  const validFiles = [];
                  let hasError = false;

                  for (const file of files) {
                    if (file.size > 5 * 1024 * 1024) {
                      setError(`File "${file.name}" exceeds 5MB limit`);
                      hasError = true;
                      break;
                    }
                    validFiles.push(file);
                  }

                  if (hasError) {
                    e.target.value = null;
                    return;
                  }

                  // Add to existing files
                  setAttachmentFiles(prev => [...prev, ...validFiles]);
                  setError('');

                  // Generate previews for images
                  const newPreviews = [];
                  validFiles.forEach((file, index) => {
                    if (file.type.startsWith('image/')) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        newPreviews.push({ file: file.name, preview: reader.result });
                        if (newPreviews.length === validFiles.filter(f => f.type.startsWith('image/')).length) {
                          setAttachmentPreviews(prev => [...prev, ...newPreviews]);
                        }
                      };
                      reader.readAsDataURL(file);
                    }
                  });

                  // Reset input for multiple selections
                  e.target.value = null;
                }
              }}
              className="form-input"
            />
            {attachmentFiles.length > 0 && (
              <div className="attachments-list">
                {attachmentFiles.map((file, index) => {
                  const preview = attachmentPreviews.find(p => p.file === file.name);
                  return (
                    <div key={index} className="attachment-item">
                      {preview ? (
                        <img src={preview.preview} alt={file.name} className="preview-image" />
                      ) : (
                        <div className="file-info">
                          <span className="file-icon">📄</span>
                          <span className="file-name">{file.name}</span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setAttachmentFiles(prev => prev.filter((_, i) => i !== index));
                          setAttachmentPreviews(prev => prev.filter(p => p.file !== file.name));
                        }}
                        className="remove-attachment-btn"
                        title="Remove file"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="submit-button"
          >
            {loading ? 'Saving... / กำลังบันทึก...' : 'Submit Training Record / บันทึกการเทรน'}
          </button>
        </form>
      </div>
      )}
    </div>
  );
}

export default TrainingForm;
